import React from 'react';
import {useMergeRefs} from '@floating-ui/react';

import {emptyObject} from '@packages/utilities/fp/utils';
import {css, cx, getSystemStyles} from '@packages/utilities/styled';
import {useTruncation} from '../../hooks';

import {Tooltip} from '../Tooltip';
import {TextView} from './Text.styles';
import type {TextProps} from './_types';

export const reset = {
  margin: 0,
  padding: 0,
  lineHeight: '1.4',
} as const;

const TextBase = React.forwardRef<
  HTMLElement,
  Omit<TextProps, 'weight' | 'translate'> & {
    truncation?: React.ComponentProps<typeof TextView>['truncation'];
  }
>(({font = 'body', size: sizeProp, ...props}, ref) => {
  return (
    <TextView {...props} data-size={sizeProp} data-text font={font} ref={ref} />
  );
});

/**
 * We are splitting this out into its own component for two reasons:
 *
 * 1) It has some hefty overhead. We do not need to add resize observers and Tooltip wrappers to every single text in our stack.
 * 2) We need a stable key to add our mergedRefs to.
 *    If we conditionally render Tooltip based on truncation needs, our key changes and our resize observer is no longer attached correctly.
 */
const TruncatedText = React.forwardRef<HTMLElement, TextProps>(
  (
    {
      as: asProp = 'span',
      children,
      style,
      truncateToLines,
      truncationLimit,
      truncationPosition = 'end',
      truncationTooltipProps = emptyObject,
      ...props
    },
    ref
  ) => {
    const {
      truncationStyles,
      content,
      didCustomTruncate,
      textRef,
      isCSSTruncated,
    } = useTruncation({
      truncateToLines,
      truncationLimit,
      truncationPosition,
      shouldNotShowTooltip: truncationTooltipProps?.shouldNotShowTooltip,
      children,
    });

    const mergedRef = useMergeRefs([textRef, ref]);

    const newStyle = React.useMemo(
      () => ({
        ...style,
        ...truncationStyles,
      }),
      [style, truncationStyles]
    );

    const shouldShowTooltip =
      !truncationTooltipProps.shouldNotShowTooltip &&
      ((didCustomTruncate && typeof children === 'string') || isCSSTruncated);

    // if an inline element is specified in addition to truncation, we want to use the truncation variant that changes the display to inline-block
    const truncationVariant =
      truncateToLines && (!asProp || asProp === 'span' || asProp === 'code')
        ? 'truncated-inline'
        : 'truncated';

    return (
      <Tooltip
        asChild
        isDisabled={!shouldShowTooltip}
        label={children}
        {...truncationTooltipProps}
      >
        <TextBase
          as={asProp}
          truncation={
            truncateToLines && Number(truncateToLines) === 1
              ? truncationVariant
              : undefined
          }
          {...props}
          ref={mergedRef}
          style={newStyle}
        >
          {content}
        </TextBase>
      </Tooltip>
    );
  }
);

/**
 * # Text
 *
 * The Text component allows developers to display text within Webflow's standardized typography guidelines
 *
 * ## Usage
 *
 * ```jsx
 * import {Text} from '@packages/systems/spring';
 *
 * const Example = () => {
 *   return (
 *     <Text>
 *       Hello world.
 *     </Text>
 *   );
 * }
 * ```
 *
 * @component
 */
export const Text = React.forwardRef<HTMLElement, TextProps>(
  (
    {
      children,
      className,
      truncateToLines,
      truncationLimit,
      truncationPosition = 'end',
      truncationTooltipProps = emptyObject,
      weight,
      style,
      ...props
    },
    ref
  ) => {
    const shouldTruncate = Boolean(
      truncateToLines || (truncationLimit && typeof children === 'string')
    );

    const styleSystemClass = React.useMemo(
      () =>
        getSystemStyles({
          ...(weight ? {fontWeight: `$${weight}`} : emptyObject), // let style system handle fontWeight if it is provided.
          ...props,
        }),
      [props, weight]
    );

    const newClassName = React.useMemo(
      () => cx(css(styleSystemClass), className),
      [styleSystemClass, className]
    );

    if (shouldTruncate) {
      return (
        <TruncatedText
          className={newClassName}
          truncateToLines={truncateToLines}
          truncationLimit={truncationLimit}
          truncationPosition={truncationPosition}
          truncationTooltipProps={truncationTooltipProps}
          {...props}
          ref={ref}
          style={style}
        >
          {children}
        </TruncatedText>
      );
    } else {
      return (
        <TextBase className={newClassName} {...props} ref={ref} style={style}>
          {children}
        </TextBase>
      );
    }
  }
);

Text.displayName = 'Text';
Text.toString = TextView.toString;
export default Text;
